import moment from 'moment';
import { Link } from 'react-router-dom';
import { useStudyPrefetch } from '@/state/hooks/useStudyPrefetch';
import { studyTypeAcronym } from '@/features/worklist';
import type { StudyView } from '@/models/view';
import styles from './RecentlyViewed.module.css';

interface RecentlyViewedProps {
  studies: StudyView[];
}

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

export default function RecentlyViewed({ studies }: RecentlyViewedProps) {
  const prefetchStudy = useStudyPrefetch();
  if (studies.length === 0) return null;

  const sorted = [...studies]
    .filter((s) => s.studyUuid)
    .sort(
      (a, b) => new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime()
    )
    .slice(0, 3);

  return (
    <section className={styles.section}>
      <h3 className={styles.heading}>Recently viewed</h3>
      <ul className={styles.list}>
        {sorted.map((study) => {
          const isStatPriority = study.priority === 'STAT';
          const isFinalized = study.reportStatus === 'FINAL';
          const dateObj = new Date(study.studyDate);

          return (
            <li key={study.studyUuid}>
              <Link
                to={`/studies/${study.studyUuid}`}
                className={`${styles.card} ${isStatPriority ? styles.priority : ''}`}
                onMouseEnter={() => prefetchStudy(study.studyUuid)}
              >
                <div className={styles.left}>
                  <div className={styles.line1}>
                    <span className={styles.name}>
                      {study.patientFirstName} {study.patientLastName}
                    </span>
                    <span className={styles.separator}>&middot;</span>
                    <span className={styles.modality}>
                      {studyTypeAcronym(study.studyModality)}
                    </span>
                  </div>
                  <div className={styles.line2}>
                    <span className={styles.site}>{study.studySite}</span>
                    <span className={styles.separator}>&middot;</span>
                    <span className={styles.shortDate}>
                      {shortDateFormatter.format(dateObj)}
                    </span>
                  </div>
                </div>
                <div className={styles.right}>
                  <span className={styles.relativeTime}>
                    {study.viewedAt ? moment(study.viewedAt).fromNow() : ''}
                  </span>
                  <span
                    className={styles.statusPill}
                    data-status={isFinalized ? 'finalized' : 'pending'}
                  >
                    {isFinalized ? 'Finalized' : 'Pending'}
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
